import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/sections/HeroSection";
import { formatEUR, ttc, DEREF_PRICE_HT } from "@/config/pricing";

const PILLARS = [
  {
    eyebrow: "Méthode",
    title: "Une démarche en quatre temps.",
    lead: "Comment HorsIndex analyse et traite une situation.",
    to: "/methode",
    cta: "Voir la méthode",
    testid: "home-apercu-methode",
  },
  {
    eyebrow: "Cadre juridique",
    title: "Le droit comme levier d'action.",
    lead: "Les droits qui peuvent permettre d'agir.",
    to: "/cadre-juridique",
    cta: "Voir le cadre juridique",
    testid: "home-apercu-droits",
  },
  {
    eyebrow: "Services",
    title: "Quatre niveaux d'accompagnement.",
    lead: "Les prestations et niveaux d'accompagnement.",
    to: "/services",
    cta: "Voir les services",
    testid: "home-apercu-services",
  },
];

/* Flux de circulation d'une information — parcours visuel continu */
const FLOW = [
  { label: "Registre officiel", phase: "Source", dot: "#B4AFA8", size: 8 },
  { label: "Open data", phase: "Source", dot: "#B4AFA8", size: 9 },
  { label: "Agrégateur", phase: "Diffusion", dot: "#CE9186", size: 10 },
  { label: "Fiche publique", phase: "Diffusion", dot: "#DC7264", size: 11 },
  { label: "Moteur de recherche", phase: "Exposition publique", dot: "#E94B3C", size: 12 },
  { label: "Rediffusion", phase: "Exposition publique", dot: "#B83A34", size: 13 },
];

function CirculationFlow() {
  return (
    <div data-testid="home-circulation-flow" className="mt-16">
      {/* Desktop — flux horizontal continu */}
      <div className="hidden md:block">
        {/* Phases */}
        <div className="grid grid-cols-6 mb-5">
          {FLOW.map((s, i) => {
            const isPhaseStart = i === 0 || FLOW[i - 1].phase !== s.phase;
            return (
              <div key={s.label}>
                {isPhaseStart ? (
                  <span
                    className={`font-mono text-[11px] tracking-[0.14em] uppercase ${
                      s.phase === "Exposition publique"
                        ? "text-[#B83A34]"
                        : "text-[#7C8086]"
                    }`}
                  >
                    {s.phase}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        {/* Ligne continue + nœuds */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute left-0 right-0 top-[7px] h-[2px] rounded-full"
            style={{
              background:
                "linear-gradient(to right, #C9C4BE 0%, #C9C4BE 30%, #DC7264 62%, #E94B3C 85%, #B83A34 100%)",
            }}
          />
          <ol className="relative grid grid-cols-6">
            {FLOW.map((s, i) => (
              <li key={s.label} data-testid={`home-flow-step-${i}`}>
                <span
                  aria-hidden="true"
                  className="block rounded-full border-2 border-[#F2F0ED]"
                  style={{
                    width: `${s.size + 4}px`,
                    height: `${s.size + 4}px`,
                    backgroundColor: s.dot,
                    marginTop: `${8 - (s.size + 4) / 2 + 1}px`,
                  }}
                />
                <p className="mt-4 font-mono text-[11px] tracking-[0.1em] uppercase text-[#7C8086]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-1 pr-4 text-[14px] font-medium text-[#181A1D] tracking-tight leading-snug">
                  {s.label}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Mobile — progression verticale continue */}
      <div className="md:hidden relative pl-7">
        <div
          aria-hidden="true"
          className="absolute left-[7px] top-1 bottom-1 w-[2px] rounded-full"
          style={{
            background:
              "linear-gradient(to bottom, #C9C4BE 0%, #C9C4BE 30%, #DC7264 62%, #E94B3C 85%, #B83A34 100%)",
          }}
        />
        <ol className="space-y-7">
          {FLOW.map((s, i) => {
            const isPhaseStart = i === 0 || FLOW[i - 1].phase !== s.phase;
            return (
              <li key={s.label} className="relative">
                <span
                  aria-hidden="true"
                  className="absolute rounded-full border-2 border-[#F2F0ED]"
                  style={{
                    width: `${s.size + 4}px`,
                    height: `${s.size + 4}px`,
                    backgroundColor: s.dot,
                    left: `${-20 - (s.size + 4) / 2 + 1}px`,
                    top: "3px",
                  }}
                />
                {isPhaseStart ? (
                  <p
                    className={`mb-1 font-mono text-[11px] tracking-[0.14em] uppercase ${
                      s.phase === "Exposition publique"
                        ? "text-[#B83A34]"
                        : "text-[#7C8086]"
                    }`}
                  >
                    {s.phase}
                  </p>
                ) : null}
                <p className="text-[15px] font-medium text-[#181A1D] tracking-tight">
                  {s.label}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 64;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  return (
    <div data-testid="page-home" className="bg-[#FAF9F7] text-[#181A1D]">
      <Navbar />
      <main>
        <HeroSection />

        {/* Le problème — comprendre l'exposition */}
        <section
          id="intro"
          data-testid="section-intro"
          className="relative bg-[#F2F0ED] py-24 lg:py-32 border-b border-[#E2DEDA]"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <p className="label-eyebrow mb-6">Le problème</p>
                <h2 className="font-sans font-semibold text-[#181A1D] text-3xl lg:text-4xl tracking-tight leading-[1.1] text-balance">
                  Vos informations peuvent circuler bien au-delà de leur source.
                </h2>
              </div>
              <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base leading-relaxed text-[#52565C]">
                <p>
                  Des informations vous concernant peuvent être reprises par
                  plusieurs sites et devenir faciles à retrouver sur Google.
                  HorsIndex identifie où elles apparaissent et examine les
                  démarches possibles pour les retirer, les masquer ou
                  réduire leur visibilité.
                </p>
                <p className="text-[14px]">
                  <Link
                    to="/comprendre"
                    data-testid="home-intro-comprendre-link"
                    className="text-[#181A1D] font-medium underline decoration-[#E94B3C]/40 underline-offset-4 hover:decoration-[#E94B3C] transition-colors"
                  >
                    Comprendre comment les informations circulent
                  </Link>
                </p>
              </div>
            </div>
            <CirculationFlow />
          </div>
        </section>

        {/* Prestations & tarifs — zone commerciale */}
        <section
          id="prestations"
          data-testid="section-prestations"
          className="relative bg-[#FAF9F7] py-24 lg:py-32"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
              <div className="lg:col-span-6">
                <p className="label-eyebrow mb-6">Prestations & tarifs</p>
                <h2 className="font-sans font-semibold text-[#181A1D] text-4xl lg:text-[44px] tracking-tight leading-[1.08] text-balance">
                  Un accompagnement gradué, des tarifs annoncés.
                </h2>
              </div>
              <div className="lg:col-span-6 flex items-end">
                <p className="text-[15px] lg:text-base text-[#52565C] leading-relaxed max-w-xl">
                  Quatre niveaux d'accompagnement, de l'audit initial à la
                  situation complexe. Les montants sont indicatifs ; le tarif
                  définitif est confirmé avant toute intervention.
                </p>
              </div>
            </div>
            <ul
              data-testid="home-prestations"
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
            >
              {[
                { t: "Audit initial", ht: 100, d: "Évaluer la situation, repérer les sources visibles et vérifier la pertinence d'une démarche." },
                { t: "Intervention ciblée", ht: 300, d: "Préparer une action limitée sur un périmètre clairement identifié." },
                { t: "Dossier complet", ht: 600, d: "Organiser un dossier lorsque plusieurs sources ou démarches sont concernées." },
                { t: "Situation complexe", ht: null, d: "Structurer une situation ancienne, dispersée ou sensible." },
              ].map((s, i) => (
                <li
                  key={s.t}
                  data-testid={`home-prestation-${i}`}
                  className="group border border-[#E2DEDA] rounded-md p-7 lg:p-8 flex flex-col bg-white hover:border-[#B9B3AC] hover:shadow-[0_2px_10px_rgba(24,26,29,0.05)] transition-[border-color,box-shadow] duration-200"
                >
                  <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#7C8086] mb-5">
                    Niveau {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-xl text-[#181A1D] font-semibold leading-snug tracking-tight mb-4">
                    {s.t}
                  </h3>
                  <p className="text-sm text-[#666A70] leading-relaxed text-pretty mb-5">
                    {s.d}
                  </p>
                  <p data-testid={`home-prestation-${i}-prix`} className="font-mono text-[14px] font-medium tracking-tight text-[#181A1D] mb-7">
                    {s.ht ? (
                      <>
                        <span className="block">À partir de {formatEUR(s.ht)}&nbsp;HT</span>
                        <span className="block mt-1 text-[12px] font-normal text-[#8B8F95]">TVA en sus au taux applicable</span>
                      </>
                    ) : (
                      "Sur devis"
                    )}
                  </p>
                  <Link
                    to="/services"
                    className="mt-auto text-[12px] font-semibold text-[#181A1D] inline-flex items-center gap-2 group-hover:text-[#B83A34] transition-colors duration-200"
                  >
                    Voir le détail
                    <ArrowRight size={13} strokeWidth={1.5} />
                  </Link>
                </li>
              ))}
            </ul>
            <div
              data-testid="home-prestation-deref"
              className="mt-4 bg-[#F6E3E0] border border-[#EDCFC9] rounded-md p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
            >
              <div className="lg:col-span-7">
                <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#B83A34] mb-4">
                  Service dédié
                </p>
                <h3 className="text-2xl lg:text-3xl text-[#181A1D] font-semibold tracking-tight">
                  Déréférencement Google
                </h3>
                <p className="mt-3 text-[15px] text-[#52565C] leading-relaxed max-w-xl">
                  Obtenir le retrait d'un résultat des recherches effectuées
                  sur votre nom, avec une argumentation individualisée pour
                  chaque URL.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col lg:items-end gap-5">
                <p data-testid="home-prestation-deref-prix" className="font-mono text-[15px] lg:text-base font-medium tracking-tight text-[#181A1D]">
                  <span className="block">À partir de {formatEUR(DEREF_PRICE_HT)}&nbsp;HT par URL</span>
                  <span className="block mt-1 text-[12px] font-normal text-[#8B8F95]">TVA en sus au taux applicable</span>
                </p>
                <Link
                  to="/desindexation"
                  data-testid="home-deref-cta"
                  className="btn-primary"
                >
                  Faire analyser un résultat
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Méthode — rupture visuelle graphite */}
        <section
          id="methode-resume"
          data-testid="section-methode-resume"
          className="relative bg-[#181A1D] py-24 lg:py-32"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
              <div className="lg:col-span-7">
                <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#E94B3C] mb-6">
                  Méthode
                </p>
                <h2 className="font-sans font-semibold text-[#FAF9F7] text-3xl lg:text-4xl xl:text-[44px] tracking-tight leading-[1.1] text-balance">
                  Comment HorsIndex procède.
                </h2>
              </div>
              <div className="lg:col-span-5 flex items-end lg:justify-end">
                <Link
                  to="/methode"
                  data-testid="home-methode-resume-link"
                  className="inline-flex items-center gap-2 border border-[#3A3D42] text-[#FAF9F7] px-6 py-3 text-[12px] tracking-[0.08em] uppercase font-medium rounded-md hover:border-[#FAF9F7] transition-colors duration-200"
                >
                  Voir la méthode
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
            <ol
              data-testid="home-methode-steps"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12"
            >
              {[
                { t: "Repérer", d: "Identifier les informations visibles et les sites concernés." },
                { t: "Analyser", d: "Vérifier leur origine, leur actualité et les démarches possibles." },
                { t: "Agir", d: "Préparer les demandes de retrait, de masquage, d'anonymisation ou de désindexation pertinentes." },
                { t: "Suivre", d: "Examiner les réponses et déterminer si une démarche complémentaire est utile." },
              ].map((s, i) => (
                <li
                  key={s.t}
                  data-testid={`home-methode-step-${i}`}
                  className="border-l border-[#2E3136] pl-6 py-1 flex flex-col"
                >
                  <span className="font-mono text-4xl lg:text-5xl font-normal text-[#E94B3C] mb-5 leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-xl text-[#FAF9F7] font-semibold leading-snug tracking-tight mb-3">
                    {s.t}
                  </h3>
                  <p className="text-sm text-[#A0A5AA] leading-relaxed text-pretty">
                    {s.d}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Aperçus des piliers internes */}
        <section
          id="apercus"
          data-testid="section-apercus"
          className="relative bg-[#FAF9F7] py-24 lg:py-32"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 lg:mb-16">
              <div className="lg:col-span-5">
                <p className="label-eyebrow mb-6">Trois axes</p>
                <h2 className="font-sans font-semibold text-[#181A1D] text-4xl lg:text-[44px] tracking-tight leading-[1.08] text-balance">
                  Aller plus loin.
                </h2>
              </div>
              <div className="lg:col-span-7 flex items-end">
                <p className="text-[15px] lg:text-base text-[#52565C] leading-relaxed max-w-2xl">
                  Trois pages pour approfondir avant de démarrer.
                </p>
              </div>
            </div>

            <ul
              data-testid="home-pillars"
              className="grid grid-cols-1 lg:grid-cols-3 gap-x-14 xl:gap-x-20 gap-y-14"
            >
              {PILLARS.map((p, i) => (
                <li key={p.to} className="relative border-t border-[#E2DEDA] pt-8">
                  <span
                    aria-hidden="true"
                    className="absolute -top-[1px] left-0 h-[2px] w-10 bg-[#E94B3C]"
                  />
                  <Link
                    to={p.to}
                    data-testid={p.testid}
                    className="group block focus:outline-none"
                  >
                    <div className="flex items-baseline justify-between mb-7">
                      <p className="label-eyebrow">{p.eyebrow}</p>
                      <span className="font-mono text-[11px] tracking-[0.14em] text-[#7C8086]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="text-[26px] lg:text-[28px] text-[#181A1D] leading-tight tracking-tight font-semibold mb-4 text-balance group-hover:text-[#B83A34] transition-colors duration-200">
                      {p.title}
                    </h3>
                    <p className="text-[15px] text-[#5A5E64] leading-relaxed text-pretty mb-7">
                      {p.lead}
                    </p>
                    <span className="text-[13px] font-medium text-[#181A1D] inline-flex items-center gap-2 underline decoration-[#D8D3CD] underline-offset-4 group-hover:decoration-[#E94B3C] transition-colors duration-200">
                      {p.cta}
                      <ArrowUpRight
                        size={14}
                        strokeWidth={1.5}
                        className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Accès Comprendre */}
        <section
          id="acces-comprendre"
          data-testid="section-acces-comprendre"
          className="relative bg-[#F2F0ED] py-20 lg:py-28 border-y border-[#E2DEDA]"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <p className="label-eyebrow mb-6">Comprendre</p>
              <h2 className="font-sans font-semibold text-[#181A1D] text-3xl lg:text-4xl tracking-tight leading-[1.1] text-balance">
                Comprendre avant d'agir.
              </h2>
              <p className="mt-7 max-w-2xl text-[15px] lg:text-base text-[#52565C] leading-relaxed">
                Comprendre pourquoi certaines informations réapparaissent
                en ligne et quels droits peuvent s'appliquer.
              </p>
              <p className="mt-5 text-[14px]">
                <Link
                  to="/simon-marie"
                  data-testid="home-simon-marie-link"
                  className="text-[#666A70] underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#181A1D] hover:decoration-[#E94B3C] transition-colors"
                >
                  Qui est derrière HorsIndex ?
                </Link>
              </p>
            </div>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <Link
                to="/comprendre"
                data-testid="home-comprendre-link"
                className="btn-ghost"
              >
                Accéder aux ressources
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA finale — audit initial */}
        <section
          id="cta-final"
          data-testid="section-cta-final"
          className="relative bg-[#181A1D]"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#E94B3C] mb-5">
                Audit initial
              </p>
              <h2 className="font-sans font-semibold text-[#FAF9F7] text-3xl lg:text-4xl xl:text-[44px] leading-[1.1] tracking-tight text-balance">
                Voir ce qui apparaît sur vous en ligne.
              </h2>
              <p className="mt-8 max-w-xl text-[15px] lg:text-base text-[#A0A5AA] leading-relaxed">
                Décrivez brièvement votre situation. L'audit initial
                identifie les informations librement accessibles à votre
                sujet, ce que HorsIndex appelle votre surface publique
                d'exposition, et évalue la pertinence d'une démarche
                ciblée. Les informations transmises servent uniquement à
                préparer un premier échange.
              </p>
            </div>
            <div className="lg:col-span-5 lg:flex lg:justify-end">
              <Link
                to="/audit-initial"
                data-testid="home-cta-final-link"
                className="btn-primary"
              >
                Demander un audit initial
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
